<template>
  <v-row justify="center">
    <!-- Main Serial Numbers Viewer Dialog -->
    <v-dialog origin="top center" v-model="dialogStatus" persistent content-class="serial-viewer-dialog"
      :max-width="dialogWidth">
      <v-card>
        <!-- Header (System Theme) -->
        <v-card-title
          class="headline dialog-custom-header white-background custom-border-bottom custom-header-blue-text d-flex align-center justify-space-between w-100">
          <div class="d-flex align-center">
            <span class="font-weight-700">
              Serial Numbers :
              {{ transactionDetail?.product || "Product Information" }}
              <span class="cyan--text" v-if="transactionDetail?.barcode">({{ transactionDetail?.barcode }})</span>
            </span>
          </div>
          <v-btn icon small @click="$emit('close:dialog')">
            <v-icon color="#000">mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="px-6 py-4">
          <!-- Stats Cards Row -->
          <v-row class="mb-2">
            <!-- Total Assigned Serials -->
            <v-col cols="12" sm="4" class="py-1 px-2">
              <div class="stat-card stat-blue d-flex align-center pa-3">
                <div class="stat-icon-wrap stat-icon-blue mr-3 d-flex align-center justify-center">
                  <v-icon color="#2563eb" size="20">mdi-cube-outline</v-icon>
                </div>
                <div>
                  <div class="stat-label">Allocated Serials</div>
                  <div class="stat-val stat-val-blue font-weight-700">
                    {{ totalAssignedCount }}
                  </div>
                </div>
              </div>
            </v-col>

            <!-- Line Item Quantity -->
            <v-col cols="12" sm="4" class="py-1 px-2">
              <div class="stat-card stat-purple d-flex align-center pa-3">
                <div class="stat-icon-wrap stat-icon-purple mr-3 d-flex align-center justify-center">
                  <v-icon color="#9333ea" size="20">mdi-clipboard-text-outline</v-icon>
                </div>
                <div>
                  <div class="stat-label">Item Quantity</div>
                  <div class="stat-val stat-val-purple font-weight-700">
                    {{ itemQuantity }}
                  </div>
                </div>
              </div>
            </v-col>

            <!-- Status -->
            <v-col cols="12" sm="4" class="py-1 px-2">
              <div class="stat-card stat-green d-flex align-center pa-3">
                <div class="stat-icon-wrap stat-icon-green mr-3 d-flex align-center justify-center">
                  <v-icon color="#16a34a" size="20">mdi-check-circle-outline</v-icon>
                </div>
                <div>
                  <div class="stat-label">Status</div>
                  <div class="stat-val font-weight-700 font-size-14"
                    :class="isMatched ? 'stat-val-green' : 'cyan--text text--darken-2'">
                    {{
                      isMatched
                        ? "Matched"
                        : `Allocated (${totalAssignedCount} / ${itemQuantity})`
                    }}
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>

          <!-- Search Bar & Expand/Collapse All Toolbar -->
          <div class="d-flex align-center justify-space-between flex-wrap my-2 px-1">
            <div>
              <v-btn
                v-if="displayedPoGroups.length > 0 || displayedGeneralSerials.length > 0"
                text
                small
                color="cyan darken-2"
                class="font-weight-600 px-2 text-capitalize"
                @click="toggleAllSections"
              >
                <v-icon small class="mr-1">
                  {{ isAllCollapsed ? "mdi-arrow-expand-vertical" : "mdi-arrow-collapse-vertical" }}
                </v-icon>
                {{ isAllCollapsed ? "Expand All" : "Collapse All" }}
              </v-btn>
            </div>

            <div style="min-width: 280px">
              <v-text-field v-model.trim="search" clearable dense outlined flat solo hide-details
                placeholder="Search Serial Number..." prepend-inner-icon="mdi-magnify" color="cyan"
                class="search-input"></v-text-field>
            </div>
          </div>

          <v-progress-linear v-if="serialLoading" indeterminate color="cyan" height="3"
            class="mb-3 rounded"></v-progress-linear>

          <!-- Scrollable PO Groups & Serial Cards Grid -->
          <perfect-scrollbar :options="{ suppressScrollX: true }" class="scroll"
            style="max-height: 52vh; position: relative">
            <!-- Empty State (No serials assigned to this line item) -->
            <div v-if="!serialLoading && totalAssignedCount === 0" class="text-center py-12">
              <img height="48" width="48" :src="$assetURL('media/error/empty.png')" class="mb-3 opacity-60" />
              <p class="m-0 font-weight-500 font-size-15 grey--text text--darken-1">
                Uhh... There are no serial numbers assigned to this item at the moment.
              </p>
            </div>

            <!-- Search Empty State (Has serials, but search matches nothing) -->
            <div v-else-if="
              !serialLoading &&
              totalAssignedCount > 0 &&
              displayedPoGroups.length === 0 &&
              displayedGeneralSerials.length === 0
            " class="text-center py-10">
              <v-icon size="40" color="grey lighten-1" class="mb-2">mdi-file-search-outline</v-icon>
              <p class="m-0 font-weight-500 font-size-14 grey--text text--darken-2">
                No serial numbers matching "{{ search }}".
              </p>
            </div>

            <!-- =========================================================
                 1. PO GROUPS (DYNAMIC FROM PURCHASE ORDER ALLOCATIONS)
            ========================================================== -->
            <div v-for="po in displayedPoGroups" :key="po.id" class="po-group mb-4">
              <!-- PO HEADER -->
              <div
                class="po-group-header pa-3 cursor-pointer"
                :class="{ 'is-collapsed': isPoCollapsed(po.id) }"
                @click="togglePoGroup(po.id)"
              >
                <div class="d-flex flex-wrap align-center justify-space-between">
                  <!-- PO Information -->
                  <div class="d-flex align-start">
                    <div class="po-icon mr-3">
                      <v-icon color="#24326d" size="20">
                        mdi-file-document-outline
                      </v-icon>
                    </div>

                    <div>
                      <div class="d-flex align-center flex-wrap">
                        <span class="po-number">
                          {{ po.po_number }}
                        </span>

                        <v-chip x-small outlined color="cyan darken-2" class="ml-2 font-weight-600">
                          {{ po.serials.length }} Serials
                        </v-chip>

                        <v-chip v-if="Number(po.allocated_qty) > 0" x-small color="cyan lighten-4"
                          class="cyan--text text--darken-3 ml-2 font-weight-600">
                          Allocated: {{ po.allocated_qty }}
                        </v-chip>
                      </div>

                      <div class="po-meta mt-1">
                        <span>
                          <v-icon x-small class="mr-1">mdi-domain</v-icon>
                          {{ po.supplier || "N/A" }}
                        </span>

                        <span class="mx-2" v-if="po.po_date">•</span>

                        <span v-if="po.po_date">
                          <v-icon x-small class="mr-1">
                            mdi-calendar-outline
                          </v-icon>
                          {{ formatDate(po.po_date) }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- Right Side: PO Allocation Summary + Chevron -->
                  <div class="d-flex align-center mt-2 mt-sm-0">
                    <div class="po-selection-summary mr-2">
                      <v-icon small color="cyan darken-2" class="mr-1">
                        mdi-check-circle-outline
                      </v-icon>
                      <span class="font-weight-700">
                        {{ po.serials.length }}
                      </span>
                      <span class="grey--text mx-1">/</span>
                      <span>
                        {{ po.allocated_qty || po.total_assigned }} Allocated
                      </span>
                    </div>

                    <v-btn icon small class="expand-btn" @click.stop="togglePoGroup(po.id)">
                      <v-icon size="22" color="#64748b">
                        {{ isPoCollapsed(po.id) ? "mdi-chevron-down" : "mdi-chevron-up" }}
                      </v-icon>
                    </v-btn>
                  </div>
                </div>
              </div>

              <!-- PO SERIALS GRID -->
              <v-expand-transition>
                <div v-show="!isPoCollapsed(po.id)" v-if="po.serials.length" class="po-serial-body pa-2">
                  <v-row>
                    <v-col v-for="(row, rowindex) in po.serials" :key="row.id || rowindex" cols="12" sm="6" md="3"
                      class="pa-2">
                      <div class="serial-card pa-3">
                        <!-- Card Top: Serial Text + Copy + QR Button -->
                        <div class="d-flex align-center justify-space-between mb-3">
                          <div class="d-flex align-center text-truncate mr-1">
                            <span class="serial-text font-weight-700 font-size-14 mr-1 text-truncate">
                              {{ getSerialText(row) }}
                            </span>
                            <v-btn icon x-small class="copy-btn ml-1" @click.stop="copyNumber(getSerialText(row))"
                              title="Copy Serial Number">
                              <v-icon size="14" color="grey darken-1">mdi-content-copy</v-icon>
                            </v-btn>
                          </div>

                          <v-btn icon x-small class="qr-btn" @click.stop="openBarcodeDialog(row)"
                            title="View Barcode / QR">
                            <v-icon size="20" color="#0891b2">mdi-qrcode-scan</v-icon>
                          </v-btn>
                        </div>

                        <!-- Serial Information (PO Barcode) -->
                        <div class="serial-info-row mb-3">
                          <div class="serial-info-item">
                            <v-icon x-small color="grey darken-1" class="mr-1">
                              mdi-package-variant-closed
                            </v-icon>
                            <span>
                              {{ po.po_number }}
                            </span>
                          </div>
                        </div>

                        <!-- Card Bottom: Allocated Badge -->
                        <div class="d-flex align-center justify-space-between">
                          <span class="in-stock-badge">Allocated</span>
                          <v-icon size="18" color="#16a34a">mdi-check-circle</v-icon>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </div>
              </v-expand-transition>
            </div>

            <!-- =========================================================
                 2. GENERAL STOCK / OTHER SERIALS SECTION
            ========================================================== -->
            <div v-if="displayedGeneralSerials.length" class="general-serial-group mt-4 mb-4">
              <div
                class="po-group-header pa-3 grey lighten-4 cursor-pointer"
                :class="{ 'is-collapsed': isGeneralCollapsed }"
                @click="toggleGeneralSection"
              >
                <div class="d-flex flex-wrap align-center justify-space-between">
                  <div class="d-flex align-center">
                    <div class="po-icon mr-3">
                      <v-icon color="#475569" size="20">
                        mdi-warehouse
                      </v-icon>
                    </div>

                    <div>
                      <div class="d-flex align-center flex-wrap">
                        <span class="po-number">
                          General Stock / Other Serials
                        </span>

                        <v-chip x-small outlined color="blue-grey darken-1" class="ml-2 font-weight-600">
                          {{ displayedGeneralSerials.length }} Serials
                        </v-chip>
                      </div>

                      <div class="po-meta mt-1">
                        <span class="caption grey--text text--darken-1">
                          Serial numbers assigned from general inventory
                        </span>
                      </div>
                    </div>
                  </div>

                  <div class="d-flex align-center mt-2 mt-sm-0">
                    <div class="po-selection-summary mr-2">
                      <v-icon small color="cyan darken-2" class="mr-1">
                        mdi-check-circle-outline
                      </v-icon>
                      <span class="font-weight-700">
                        {{ displayedGeneralSerials.length }}
                      </span>
                      <span class="grey--text mx-1">/</span>
                      <span>
                        {{ totalGeneralAssigned }} Allocated
                      </span>
                    </div>

                    <v-btn icon small class="expand-btn" @click.stop="toggleGeneralSection">
                      <v-icon size="22" color="#64748b">
                        {{ isGeneralCollapsed ? "mdi-chevron-down" : "mdi-chevron-up" }}
                      </v-icon>
                    </v-btn>
                  </div>
                </div>
              </div>

              <!-- General Serials Body -->
              <v-expand-transition>
                <div v-show="!isGeneralCollapsed" class="po-serial-body pa-2">
                  <v-row>
                    <v-col v-for="(row, rowindex) in displayedGeneralSerials" :key="row.id || rowindex" cols="12" sm="6"
                      md="3" class="pa-2">
                      <div class="serial-card pa-3">
                        <!-- Card Top: Serial Text + Copy + QR Button -->
                        <div class="d-flex align-center justify-space-between mb-3">
                          <div class="d-flex align-center text-truncate mr-1">
                            <span class="serial-text font-weight-700 font-size-14 mr-1 text-truncate">
                              {{ getSerialText(row) }}
                            </span>
                            <v-btn icon x-small class="copy-btn ml-1" @click.stop="copyNumber(getSerialText(row))"
                              title="Copy Serial Number">
                              <v-icon size="14" color="grey darken-1">mdi-content-copy</v-icon>
                            </v-btn>
                          </div>

                          <v-btn icon x-small class="qr-btn" @click.stop="openBarcodeDialog(row)"
                            title="View Barcode / QR">
                            <v-icon size="20" color="#0891b2">mdi-qrcode-scan</v-icon>
                          </v-btn>
                        </div>

                        <!-- Serial Information (General Stock) -->
                        <div class="serial-info-row mb-3">
                          <div class="serial-info-item">
                            <v-icon x-small color="grey darken-1" class="mr-1">
                              mdi-warehouse
                            </v-icon>
                            <span>General Stock</span>
                          </div>
                        </div>

                        <!-- Card Bottom: Allocated Badge -->
                        <div class="d-flex align-center justify-space-between">
                          <span class="in-stock-badge">Allocated</span>
                          <v-icon size="18" color="#16a34a">mdi-check-circle</v-icon>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </div>
              </v-expand-transition>
            </div>
          </perfect-scrollbar>
        </v-card-text>

        <!-- Footer (System Theme Buttons) -->
        <v-divider class="my-0"></v-divider>
        <v-card-actions class="px-6 py-3">
          <div class="d-flex align-center">
            <v-icon color="cyan" size="20" class="mr-2">mdi-pound</v-icon>
            <span class="font-weight-700 font-size-14 custom-header-blue-text">
              Total {{ totalAssignedCount }} Serial Number(s)
            </span>
          </div>

          <v-spacer></v-spacer>

          <v-btn depressed class="mx-2 custom-grey-border custom-bold-button" @click="$emit('close:dialog')">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Barcode / QR Popup Dialog -->
    <v-dialog v-model="barcodeDialog" max-width="420" persistent content-class="barcode-popup-dialog">
      <v-card>
        <!-- Header -->
        <v-card-title
          class="headline dialog-custom-header white-background custom-border-bottom custom-header-blue-text d-flex align-center justify-space-between w-100">
          <span class="font-weight-700">Barcode</span>
          <v-btn icon small @click="barcodeDialog = false">
            <v-icon color="#000">mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <!-- Preview Box -->
        <v-card-text class="px-6 py-4">
          <div class="barcode-preview-box pa-6 d-flex flex-column align-center justify-center">
            <img v-if="selectedRowBarcodeUrl" :src="selectedRowBarcodeUrl"
              style="max-width: 220px; max-height: 220px; object-fit: contain" alt="Barcode/QR" />
            <div v-else class="text-center py-6">
              <v-icon size="96" color="#334155">mdi-qrcode</v-icon>
            </div>

            <div class="serial-pill-badge mt-4">
              {{ selectedRowSerialText }}
            </div>
          </div>
        </v-card-text>

        <v-divider class="my-0"></v-divider>
        <v-card-actions class="px-4 py-2">
          <v-spacer></v-spacer>
          <v-btn depressed class="mx-2 custom-grey-border custom-bold-button" @click="barcodeDialog = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import CommonMixin from "@/core/plugins/common-mixin";
import { SuccessEventBus } from "@/core/lib/message.lib";
import { QUERY } from "@/core/services/store/request.module";

export default {
  name: "View-Serial-Number",
  mixins: [CommonMixin],
  props: {
    type: {
      type: String,
      default: null,
    },
    dialogStatus: {
      type: Boolean,
      default: false,
    },
    transactionDetail: {
      type: Object,
      default: () => ({}),
    },
    parentId: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      search: null,
      barcodeDialog: false,
      selectedRowBarcodeUrl: null,
      selectedRowSerialText: null,

      serialLoading: false,
      apiPoGroups: [],
      apiGeneralSerials: [],
      apiRows: [],
      collapsedPoGroups: [],
      isGeneralCollapsed: false,
    };
  },
  watch: {
    dialogStatus: {
      deep: true,
      immediate: true,
      handler(param) {
        if (param) {
          this.search = null;
          this.apiPoGroups = [];
          this.apiGeneralSerials = [];
          this.apiRows = [];
          this.collapsedPoGroups = [];
          this.isGeneralCollapsed = false;
          this.get_numbers();
        }
      },
    },
  },
  computed: {
    dialogWidth() {
      return (
        this.lodash.toSafeInteger((document.body.clientWidth / 100) * 70) || 980
      );
    },

    itemQuantity() {
      return Number(this.transactionDetail?.quantity || 0);
    },

    rawAssignedSerials() {
      const serials =
        this.transactionDetail?.serialize_number ||
        this.transactionDetail?.serialNumber ||
        this.transactionDetail?.adjust_serial_array ||
        this.transactionDetail?.selected_serial_ids ||
        [];
      return Array.isArray(serials) ? serials : [];
    },

    assignedSerialIds() {
      const ids = [];
      this.rawAssignedSerials.forEach((item) => {
        if (typeof item === "object" && item !== null) {
          const id = Number(item.serial_number_id || item.id || 0);
          if (id > 0) ids.push(id);
        } else if (Number(item) > 0) {
          ids.push(Number(item));
        }
      });
      return Array.from(new Set(ids));
    },

    totalAssignedCount() {
      return this.assignedSerialIds.length;
    },

    isMatched() {
      return (
        this.totalAssignedCount === this.itemQuantity &&
        this.itemQuantity > 0
      );
    },

    localSerialMap() {
      const map = new Map();
      this.rawAssignedSerials.forEach((s) => {
        if (typeof s === "object" && s !== null) {
          const id = Number(s.serial_number_id || s.id || 0);
          if (id > 0) {
            const resolved = s.product_serialize_number
              ? { ...s.product_serialize_number, ...s }
              : s;
            map.set(id, resolved);
          }
        }
      });
      return map;
    },

    allKnownSerialsMap() {
      const map = new Map();
      (this.apiRows || []).forEach((row) => {
        if (row?.id) map.set(Number(row.id), row);
      });
      this.localSerialMap.forEach((val, key) => {
        if (!map.has(key) || !map.get(key)?.text) {
          map.set(key, { ...map.get(key), ...val });
        }
      });
      return map;
    },

    /*
     * =========================================================
     * PO-WISE GROUPING OF ASSIGNED SERIALS
     * =========================================================
     */
    allPoGroups() {
      const assignedIds = new Set(this.assignedSerialIds);

      // 1. Group using API PO Groups if available
      if (this.apiPoGroups && this.apiPoGroups.length > 0) {
        const groups = [];
        this.apiPoGroups.forEach((po) => {
          const allAssignedForPo = (po.serials || []).filter((s) =>
            assignedIds.has(Number(s.id))
          );

          if (allAssignedForPo.length > 0) {
            groups.push({
              id: po.id,
              po_number: po.po_number || `PO-${po.id}`,
              supplier: po.supplier || "N/A",
              po_date: po.po_date || "",
              allocated_qty: Number(po.allocated_qty || 0),
              total_assigned: allAssignedForPo.length,
              serials: allAssignedForPo,
            });
          }
        });
        return groups;
      }

      return [];
    },

    displayedPoGroups() {
      const q = (this.search || "").toLowerCase().trim();
      if (!q) return this.allPoGroups;

      const groups = [];
      this.allPoGroups.forEach((po) => {
        const matchingSerials = (po.serials || []).filter((s) => {
          const text = this.getSerialText(s).toLowerCase();
          return text.includes(q);
        });

        if (matchingSerials.length > 0) {
          groups.push({
            ...po,
            serials: matchingSerials,
          });
        }
      });
      return groups;
    },

    assignedPoSerialIdSet() {
      const set = new Set();
      this.allPoGroups.forEach((po) => {
        (po.serials || []).forEach((s) => {
          set.add(Number(s.id));
        });
      });
      return set;
    },

    /*
     * =========================================================
     * GENERAL STOCK SERIALS (ASSIGNED BUT NOT BELONGING TO PO)
     * =========================================================
     */
    allGeneralAssignedSerials() {
      const poIds = this.assignedPoSerialIdSet;
      const list = [];
      this.assignedSerialIds.forEach((id) => {
        if (!poIds.has(id)) {
          const serial = this.allKnownSerialsMap.get(id) || { id, text: `SN-${id}` };
          list.push(serial);
        }
      });
      return list;
    },

    totalGeneralAssigned() {
      return this.allGeneralAssignedSerials.length;
    },

    displayedGeneralSerials() {
      const q = (this.search || "").toLowerCase().trim();
      return this.allGeneralAssignedSerials.filter((s) => {
        if (!q) return true;
        const text = this.getSerialText(s).toLowerCase();
        return text.includes(q);
      });
    },

    isAllCollapsed() {
      const allPoCollapsed =
        !this.displayedPoGroups.length ||
        this.displayedPoGroups.every((po) => this.isPoCollapsed(po.id));
      const generalCollapsed =
        !this.displayedGeneralSerials.length || this.isGeneralCollapsed;
      return allPoCollapsed && generalCollapsed;
    },
  },
  methods: {
    get_numbers() {
      this.serialLoading = true;

      const visitId =
        this.transactionDetail?.visit_id ||
        this.transactionDetail?.visit ||
        this.transactionDetail?.parent ||
        this.parentId ||
        this.$route?.query?.visit ||
        this.$route?.params?.visit ||
        0;

      const lineItemId = this.transactionDetail?.id || 0;

      const productId = Number(
        this.transactionDetail?.product_id ||
        this.transactionDetail?.product?.id ||
        (typeof this.transactionDetail?.product === "number"
          ? this.transactionDetail.product
          : 0) ||
        this.parentId ||
        0
      );

      if (!productId) {
        this.serialLoading = false;
        return;
      }

      this.$store
        .dispatch(QUERY, {
          url: `serial-number/${productId}`,
          data: {
            visit: visitId,
            line_item: lineItemId,
            required_qty: Number(this.transactionDetail?.quantity || 0),
          },
        })
        .then((response) => {
          this.apiPoGroups = Array.isArray(response?.data?.po_groups)
            ? response.data.po_groups
            : [];
          this.apiGeneralSerials = Array.isArray(
            response?.data?.general_serials
          )
            ? response.data.general_serials
            : [];
          this.apiRows = Array.isArray(response?.data?.rows)
            ? response.data.rows
            : [];
        })
        .catch((error) => {
          console.error("Error fetching serial numbers in view dialog:", error);
        })
        .finally(() => {
          this.serialLoading = false;
        });
    },

    getSerialText(row) {
      if (!row) return "";
      if (typeof row === "string") return row;
      return (
        row?.text ||
        row?.serial_number ||
        row?.product_serialize_number?.text ||
        row?.product_serialize_number?.serial_number ||
        row?.serial_number_id ||
        row?.id ||
        ""
      );
    },

    copyNumber(data) {
      if (!data) return;
      navigator.clipboard.writeText(String(data));
      SuccessEventBus.$emit("update:success", `${data} successfully copied.`);
    },

    openBarcodeDialog(row) {
      this.selectedRowSerialText = this.getSerialText(row) || "N/A";
      this.selectedRowBarcodeUrl =
        row?.barcode_image ||
        row?.product_serialize_number?.barcode_image ||
        null;
      this.barcodeDialog = true;
    },

    togglePoGroup(poId) {
      const index = this.collapsedPoGroups.indexOf(poId);
      if (index > -1) {
        this.collapsedPoGroups.splice(index, 1);
      } else {
        this.collapsedPoGroups.push(poId);
      }
    },

    isPoCollapsed(poId) {
      return this.collapsedPoGroups.includes(poId);
    },

    toggleGeneralSection() {
      this.isGeneralCollapsed = !this.isGeneralCollapsed;
    },

    toggleAllSections() {
      if (this.isAllCollapsed) {
        this.collapsedPoGroups = [];
        this.isGeneralCollapsed = false;
      } else {
        this.collapsedPoGroups = this.displayedPoGroups.map((p) => p.id);
        this.isGeneralCollapsed = true;
      }
    },
  },
};
</script>

<style scoped>
.v-dialog__content {
  align-items: start;
}

/* Stat Cards */
.stat-card {
  border-radius: 12px;
  transition: transform 0.15s ease;
}

.stat-blue {
  background: #f0f9ff;
  border: 1px solid #bae6fd;
}

.stat-icon-blue {
  width: 36px;
  height: 36px;
  background: #e0f2fe;
  border-radius: 8px;
}

.stat-val-blue {
  color: #0284c7;
  font-size: 18px;
}

.stat-green {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
}

.stat-icon-green {
  width: 36px;
  height: 36px;
  background: #dcfce7;
  border-radius: 8px;
}

.stat-val-green {
  color: #16a34a;
  font-size: 18px;
}

.stat-purple {
  background: #faf5ff;
  border: 1px solid #e9d5ff;
}

.stat-icon-purple {
  width: 36px;
  height: 36px;
  background: #f3e8ff;
  border-radius: 8px;
}

.stat-val-purple {
  color: #9333ea;
  font-size: 18px;
}

.stat-label {
  font-size: 11px;
  color: #64748b;
  font-weight: 500;
  line-height: 1.2;
}

/* Search input */
.search-input>>>.v-input__control>.v-input__slot {
  border-color: #cbd5e1 !important;
  min-height: 38px !important;
}

/* PO Group */
.po-group {
  border: 1px solid #dbe4ee;
  border-radius: 12px;
  background: #ffffff;
  overflow: hidden;
}

.general-serial-group {
  border: 1px solid #dbe4ee;
  border-radius: 12px;
  background: #ffffff;
  overflow: hidden;
}

.po-group-header {
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s ease;
}

.po-group-header:hover {
  background: #f1f5f9;
}

.po-group-header.is-collapsed {
  border-bottom: none;
}

.po-icon {
  width: 38px;
  height: 38px;
  border-radius: 8px;
  background: #eef2ff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.po-number {
  color: #24326d;
  font-size: 15px;
  font-weight: 700;
}

.po-meta {
  color: #64748b;
  font-size: 12px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.po-selection-summary {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #475569;
  background: #ffffff;
  border: 1px solid #dbe4ee;
  border-radius: 6px;
  padding: 5px 9px;
}

/* Serial Body */
.po-serial-body {
  background: #ffffff;
}

/* Serial Cards */
.serial-card {
  background: #f4fbf7;
  border: 1.5px solid #d1fae5;
  border-radius: 10px;
  transition: all 0.15s ease-in-out;
}

.serial-card:hover {
  border-color: #059669;
  box-shadow: 0 4px 12px rgba(5, 150, 105, 0.12);
  transform: translateY(-1px);
}

.serial-text {
  color: #166534;
}

.serial-info-row {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 5px 7px;
}

.serial-info-item {
  display: flex;
  align-items: center;
  color: #64748b;
  font-size: 10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.in-stock-badge {
  background: #15803d;
  color: white;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
}

.copy-btn,
.qr-btn {
  background: transparent;
}

/* Barcode Modal */
.barcode-preview-box {
  background: #ffffff;
  border: 1.5px dashed #cbd5e1;
  border-radius: 16px;
}

.serial-pill-badge {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1d4ed8;
  font-weight: 700;
  font-size: 13px;
  border-radius: 20px;
  padding: 4px 22px;
}
</style>
